// Domain components shared by more than one page.
import React from "react";
import { CheckCircle2, LogIn, LogOut, MapPin, Star } from "lucide-react";
import { Button, DataTable, PersonCell, Progress, StatusBadge } from "@/components/ui";
import { dateLabel, daysUntil, hms, hours, time } from "@/lib/format";
import { cn } from "@/lib/utils";

export const DEPARTMENT_OPTIONS = [
  { value: "all", label: "All departments" },
  ...["HR", "Training", "Sales", "Operations", "Accounts"].map((d) => ({ value: d, label: d })),
];

/** Punch-in/out control on the brand gradient. Takes the state returned by usePunchClock. */
export function PunchCard({ clock, compact = false }) {
  const { today, punchedIn, punchedOut, elapsed, pending, punchIn, punchOut } = clock;
  const size = compact ? "text-3xl" : "text-4xl";
  return (
    <div
      className={cn("rounded-2xl text-white relative overflow-hidden shadow-neu-brand", compact ? "p-5" : "p-6")}
      style={{ background: "linear-gradient(145deg,#225881,#1a4363)" }}
    >
      <div className="absolute -top-12 -right-10 h-40 w-40 rounded-full bg-brand-orange/25 blur-2xl pointer-events-none" />
      <div className="relative">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/60">Working hours today</p>
        {punchedOut ? (
          <p className={cn("font-extrabold mt-2 tabular-nums", size)}>{hours(today.working_hours)} <span className="text-base font-semibold text-white/70">hrs</span></p>
        ) : punchedIn ? (
          <p className={cn("font-extrabold mt-2 tabular-nums text-brand-orange", size)}>{hms(elapsed)}</p>
        ) : (
          <p className={cn("font-extrabold mt-2 tabular-nums text-white/35", size)}>--:--:--</p>
        )}
        <p className="text-[11px] text-white/70 mt-2 flex items-center gap-1.5">
          <MapPin size={12} className="shrink-0" />
          <span className="truncate">
            {punchedIn ? `In ${time(today.punch_in)}${punchedOut ? ` · Out ${time(today.punch_out)}` : ""} · ${today.location}` : "Not punched in yet"}
          </span>
        </p>
        <div className="mt-4">
          {!punchedIn && <Button variant="accent" size="md" loading={pending} icon={<LogIn size={15} />} onClick={punchIn}>Punch in</Button>}
          {punchedIn && !punchedOut && <Button size="md" loading={pending} icon={<LogOut size={15} />} onClick={punchOut}>Punch out</Button>}
          {punchedOut && <span className="inline-flex items-center gap-2 text-xs font-semibold text-white/85"><CheckCircle2 size={15} className="text-emerald-300" /> Day complete</span>}
        </div>
      </div>
    </div>
  );
}

export function HolidayList({ holidays, limit }) {
  const list = limit ? holidays.slice(0, limit) : holidays;
  if (!list.length) return <p className="text-xs text-slate-500 py-4 text-center">No upcoming holidays.</p>;
  return (
    <ul className="space-y-1">
      {list.map((h) => (
        <li key={h.date} className="flex items-center gap-2.5 p-2 rounded-2xl transition-all hover:shadow-neu-xs">
          <div className="h-10 w-10 shrink-0 rounded-xl shadow-neu-inset-xs flex flex-col items-center justify-center">
            <span className="text-sm font-extrabold text-brand-orange leading-none">{Number(h.date.slice(8))}</span>
            <span className="text-[9px] font-bold uppercase text-slate-400">{new Date(`${h.date}T00:00:00`).toLocaleDateString("en-GB", { month: "short" })}</span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold text-brand-blue truncate">{h.name}</p>
            <p className="text-[11px] text-slate-500">{h.type} · in {daysUntil(h.date)} days</p>
          </div>
          {h.type === "Public" && <Star size={12} className="text-brand-orange fill-current shrink-0" />}
        </li>
      ))}
    </ul>
  );
}

/** Admin-panel "breakdown" rows: label, recessed bar, value. */
export function LeaveBalanceBars({ balance }) {
  return (
    <div className="space-y-2.5">
      {balance.map((b) => (
        <div key={b.leave_type} className="flex items-center gap-2">
          <span className="w-24 shrink-0 text-[10px] font-semibold text-slate-500">{b.leave_type}</span>
          <Progress className="flex-1" value={b.entitled ? (b.balance / b.entitled) * 100 : 0} barClassName="bg-brand-blue" />
          <span className="w-14 text-right text-[10px] font-extrabold text-brand-blue tabular-nums shrink-0">{b.balance}/{b.entitled}</span>
        </div>
      ))}
    </div>
  );
}

/** Tile grid of leave balances. */
export function LeaveBalanceGrid({ balance, className }) {
  return (
    <div className={cn("grid grid-cols-2 md:grid-cols-3 gap-3", className)}>
      {balance.map((b) => (
        <div key={b.leave_type} className="neu-inset p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">{b.leave_type}</p>
          <div className="flex items-baseline gap-1.5 mt-1.5">
            <span className="text-2xl font-extrabold text-brand-blue tabular-nums">{b.balance}</span>
            <span className="text-[11px] text-slate-500">/ {b.entitled} days</span>
          </div>
          <div className="mt-3 h-1.5 rounded-full bg-slate-200/70 overflow-hidden">
            <div className="h-full rounded-full bg-brand-orange transition-all duration-700" style={{ width: `${b.entitled ? Math.min(100, (b.used / b.entitled) * 100) : 0}%` }} />
          </div>
          <p className="text-[11px] text-slate-500 mt-2">Used {b.used}</p>
        </div>
      ))}
    </div>
  );
}

/** Attendance rows. Pass `employees` (id → employee map) to show a person column. */
export function AttendanceTable({ records, employees, maxRows, empty = "No attendance records" }) {
  const columns = [
    { key: "date", header: "Date", render: (r) => <span className="text-xs font-semibold text-brand-blue">{dateLabel(r.date)}</span> },
    ...(employees ? [
      { key: "employee", header: "Employee", render: (r) => <PersonCell name={employees[r.employee_id]?.name || r.employee_id} sub={employees[r.employee_id]?.department} /> },
    ] : []),
    { key: "in", header: "In", render: (r) => <span className="font-mono text-xs">{time(r.punch_in)}</span> },
    { key: "out", header: "Out", render: (r) => <span className="font-mono text-xs">{time(r.punch_out)}</span> },
    { key: "hours", header: "Hours", render: (r) => <span className="text-xs font-extrabold text-brand-blue tabular-nums">{hours(r.working_hours)}</span> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    ...(!employees ? [{ key: "location", header: "Location", hide: "lg", render: (r) => <span className="text-[11px] text-slate-500">{r.location}</span> }] : []),
  ];
  return <DataTable columns={columns} rows={records} maxRows={maxRows} empty={empty} />;
}

export function DocumentExpiryTable({ documents, maxRows }) {
  const columns = [
    { key: "employee", header: "Employee", render: (d) => <PersonCell name={d.employee_name} sub={d.employee_id} /> },
    { key: "document", header: "Document", render: (d) => <span className="text-xs font-semibold">{d.document}</span> },
    { key: "expiry_date", header: "Expiry", hide: "md", render: (d) => <span className="text-xs">{dateLabel(d.expiry_date)}</span> },
    {
      key: "days", header: "Days left", render: (d) => {
        const n = daysUntil(d.expiry_date);
        return <span className={cn("text-xs font-extrabold tabular-nums", n < 0 ? "text-rose-600" : n < 14 ? "text-amber-600" : "text-brand-blue")}>{n < 0 ? `${-n} overdue` : n}</span>;
      },
    },
    { key: "status", header: "Status", render: (d) => <StatusBadge status={d.status} /> },
  ];
  return (
    <DataTable
      columns={columns}
      rows={documents}
      rowKey={(d) => `${d.employee_id}-${d.document}`}
      maxRows={maxRows}
      empty={{ title: "All documents are current", message: "Nothing expires in the next 30 days." }}
    />
  );
}
